import React, { useState, useMemo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Switch,
  Box,
  CircularProgress,
  Typography,
  Alert,
  Chip,
} from "@mui/material";
import { Edit as EditIcon, Delete as DeleteIcon } from "@mui/icons-material";
import { useSelector } from "react-redux";
import { RootState } from "../../../store";
import { canPerformAction, ActionKey } from "../../../auth/actionMatrix";
import { Requisite } from "../../../types/requisite";
import { Trader } from "../../../types/trader";
import { PaymentSystem, Bank } from "../../../types/common/payment";
import DeleteConfirmationDialog from "./DeleteConfirmationDialog";
import RequisiteQrButton from "./RequisiteQrButton";
import { useNotification } from "../../../contexts/NotificationContext";

interface RequisitesTableProps {
  requisites: Requisite[];
  traders: Trader[];
  paymentSystems: PaymentSystem[];
  banks: Bank[];
  loading?: boolean;
  error?: string | null;
  onEdit: (requisite: Requisite) => void;
  onDelete: (id: string) => void;
  onToggleActive: (id: string, newState: boolean) => void;
  onUnblock: (id: string) => void;
}

export default function RequisitesTable({
  requisites,
  traders,
  paymentSystems,
  banks,
  loading = false,
  error = null,
  onEdit,
  onDelete,
  onToggleActive,
  onUnblock,
}: RequisitesTableProps) {
  const currentUser = useSelector((state: RootState) => state.auth.user);
  const { showNotification } = useNotification();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [requisiteToDelete, setRequisiteToDelete] = useState<Requisite | null>(
    null
  );

  const canToggleActive = useMemo(() => canPerformAction(currentUser, ActionKey.REQUISITES_TOGGLE_ACTIVE), [currentUser]);
  const canUnblock = canPerformAction(currentUser, ActionKey.REQUISITES_UNBLOCK);
  const canUpdate = useMemo(() => canPerformAction(currentUser, ActionKey.REQUISITES_UPDATE), [currentUser]);
  const canDelete = useMemo(() => canPerformAction(currentUser, ActionKey.REQUISITES_DELETE), [currentUser]);

  const handleDeleteClick = (requisite: Requisite) => {
    setRequisiteToDelete(requisite);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (requisiteToDelete) {
      onDelete(requisiteToDelete.id);
      setDeleteDialogOpen(false);
      setRequisiteToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setRequisiteToDelete(null);
  };

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <>
      <TableContainer component={Paper} sx={{ boxShadow: 3, position: 'relative' }}>
        {loading && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'rgba(0, 0, 0, 0.2)',
              zIndex: 1,
            }}
          >
            <CircularProgress />
          </Box>
        )}
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Название</TableCell>
              <TableCell>Трейдер</TableCell>
              <TableCell>Платежная система</TableCell>
              <TableCell>Номер</TableCell>
              <TableCell>Владелец</TableCell>
              <TableCell>Банк</TableCell>
              <TableCell>Лимиты</TableCell>
              <TableCell>Статус</TableCell>
              <TableCell>Создан</TableCell>
              <TableCell>Обновлён</TableCell>
              <TableCell>Действия</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!loading && requisites.length === 0 && (
              <TableRow>
                <TableCell colSpan={11} align="center">
                  <Typography color="text.secondary" sx={{ py: 3 }}>
                    Реквизиты не найдены
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {requisites.map((requisite) => (
              <TableRow key={requisite.id}>
                <TableCell>{requisite.name}{requisite.has_qr && <RequisiteQrButton id={requisite.id} />}</TableCell>
                <TableCell>
                  {traders.find((t) => t.id === requisite.trader_id)?.name ||
                    "Не указан"}
                </TableCell>
                <TableCell>
                  {paymentSystems.find(
                    (ps) => ps.id === requisite.payment_system_id
                  )?.token || "Не указана"}
                </TableCell>
                <TableCell>{requisite.second_number || "-"}</TableCell>
                <TableCell>{requisite.holder_name}</TableCell>
                <TableCell>
                  {banks.find((b) => b.id === requisite.bank_id)?.name ||
                    requisite.bank_id}
                </TableCell>
                <TableCell>
                  {requisite.min_transaction_limit} -{" "}
                  {requisite.max_transaction_limit}
                </TableCell>
                <TableCell>
                  <Tooltip
                    title={
                      requisite.is_blocked_by_trader
                        ? canUnblock
                          ? 'Снять UPI-блокировку и активировать'
                          : 'UPI ID заблокирован трейдером; снять блокировку может только администратор'
                        : canToggleActive
                          ? requisite.is_active
                            ? 'Деактивировать'
                            : 'Активировать'
                          : 'Недостаточно прав'
                    }
                  >
                    <span>
                      <Switch
                        checked={requisite.is_active}
                        onChange={(e) => {
                          if (requisite.is_blocked_by_trader) {
                            if (canUnblock && e.target.checked) {
                              onUnblock(requisite.id);
                            }
                            return;
                          }
                          if (!canToggleActive) {
                            showNotification('У вас нет прав для изменения статуса реквизитов', 'error');
                            return;
                          }
                          onToggleActive(requisite.id, e.target.checked);
                        }}
                        size="small"
                        disabled={requisite.is_blocked_by_trader ? !canUnblock : !canToggleActive}
                      />
                    </span>
                  </Tooltip>
                  {requisite.is_blocked_by_trader && (
                    <Chip
                      label="UPI ID Blockage"
                      color="error"
                      size="small"
                      sx={{ ml: 1 }}
                    />
                  )}
                </TableCell>
                <TableCell>{new Date(requisite.created_at).toLocaleString('ru-RU')}</TableCell>
                <TableCell>{new Date(requisite.updated_at).toLocaleString('ru-RU')}</TableCell>
                <TableCell>
                  <Tooltip title={canUpdate ? "Редактировать" : "Недостаточно прав"}>
                    <span>
                      <IconButton 
                        size="small" 
                        onClick={() => {
                          if (!canUpdate) {
                            showNotification('У вас нет прав для редактирования реквизитов', 'error');
                            return;
                          }
                          onEdit(requisite);
                        }}
                        disabled={!canUpdate}
                      >
                        <EditIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title={canDelete ? "Удалить" : "Недостаточно прав"}>
                    <span>
                      <IconButton
                        size="small"
                        onClick={() => {
                          if (!canDelete) {
                            showNotification('У вас нет прав для удаления реквизитов', 'error');
                            return;
                          }
                          handleDeleteClick(requisite);
                        }}
                        sx={{ color: "#ff4444" }}
                        disabled={!canDelete}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <DeleteConfirmationDialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title={requisiteToDelete?.name || ""}
      />
    </>
  );
}
